export const formatDate = (dateString) => {
	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now - date;
	const diffHours = diffMs / (1000 * 60 * 60);

	if (diffHours < 24) {
		if (diffHours < 1) {
			const mins = Math.round(diffMs / (1000 * 60));
			return `${mins} minutes ago`;
		}
		return `${Math.round(diffHours)}h ago`;
	}
	return date.toLocaleDateString();
};

// Returns the URL only if it uses a safe http(s) scheme, otherwise undefined.
// Guards against javascript:/data: URLs being used as bare hrefs.
export const safeHttpUrl = (url) => {
	if (!url || typeof url !== "string") return undefined;
	return /^https?:\/\//i.test(url.trim()) ? url : undefined;
};

export const formatProjectName = (reference) => {
	const parts = reference.split("/");
	if (parts.length <= 1) return reference;

	const projectPart = parts.join("/").split("!")[0];
	return projectPart;
};

export const isStale = (mr) => {
	if (!mr || mr.state !== "opened") return false;

	const now = new Date();
	const updatedAt = new Date(mr.updatedAt);
	const daysSinceUpdate = (now - updatedAt) / (1000 * 60 * 60 * 24);

	return daysSinceUpdate > 14;
};

// Escape a string for safe inclusion in HTML.
const escapeHtml = (str) =>
	String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

// Build the visible label for a copied MR link, e.g. "group/project #1234: Title".
export const formatMrLinkLabel = (mr) => {
	const project = formatProjectName(mr.projectPath || "");
	const prefix = project ? `${project} ` : "";
	return `${prefix}#${mr.iid}: ${mr.title}`;
};

const normalizeClipboardText = (text) =>
	typeof text === "string" ? text : String(text ?? "");

const getNativeClipboard = () => {
	if (typeof window === "undefined") return null;
	return window.gitlabDashNative?.clipboard || null;
};

const copyTextWithExecCommand = (text) => {
	if (typeof document === "undefined" || !document.body) return false;

	const activeElement = document.activeElement;
	const selection = window.getSelection?.();
	const selectedRanges = [];
	if (selection) {
		for (let i = 0; i < selection.rangeCount; i += 1) {
			selectedRanges.push(selection.getRangeAt(i).cloneRange());
		}
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.left = "-9999px";
	textarea.style.top = "0";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);

	let copied = false;
	try {
		textarea.focus();
		textarea.select();
		copied = document.execCommand("copy");
	} catch (error) {
		console.warn("Fallback clipboard copy failed:", error);
	} finally {
		document.body.removeChild(textarea);
		if (selection) {
			selection.removeAllRanges();
			for (const range of selectedRanges) selection.addRange(range);
		}
		if (activeElement?.focus) {
			activeElement.focus({ preventScroll: true });
		}
	}

	return copied;
};

// Copy plain text, with native desktop and execCommand fallbacks for contexts
// where the async Clipboard API is missing or denied. Optionally accepts HTML
// so rich links paste cleanly into editors that support text/html.
export const copyTextToClipboard = async (text, { html } = {}) => {
	const normalizedText = normalizeClipboardText(text);
	if (!normalizedText) return false;

	const nativeClipboard = getNativeClipboard();
	if (nativeClipboard) {
		try {
			if (html && nativeClipboard.write) {
				return Boolean(
					await nativeClipboard.write({ text: normalizedText, html }),
				);
			}
			if (nativeClipboard.writeText) {
				return Boolean(await nativeClipboard.writeText(normalizedText));
			}
		} catch (error) {
			console.warn(
				"Native clipboard copy failed; trying web clipboard:",
				error,
			);
		}
	}

	const clipboard =
		typeof navigator !== "undefined" ? navigator.clipboard : null;
	if (html && typeof ClipboardItem !== "undefined" && clipboard?.write) {
		try {
			await clipboard.write([
				new ClipboardItem({
					"text/html": new Blob([html], { type: "text/html" }),
					"text/plain": new Blob([normalizedText], { type: "text/plain" }),
				}),
			]);
			return true;
		} catch (error) {
			console.warn("Rich clipboard copy failed; trying plain text:", error);
		}
	}

	if (clipboard?.writeText) {
		try {
			await clipboard.writeText(normalizedText);
			return true;
		} catch (error) {
			console.warn("Clipboard writeText failed; trying fallback copy:", error);
		}
	}

	return copyTextWithExecCommand(normalizedText);
};

export const getMRLink = (mr) => safeHttpUrl(mr?.webUrl || mr?.url);

export const formatMRLinksText = (mrs) =>
	(Array.isArray(mrs) ? mrs : [mrs]).map(getMRLink).filter(Boolean).join("\n");

export const copyMRLinks = async (mrs) =>
	copyTextToClipboard(formatMRLinksText(mrs));

// Copy an MR as a rich-text hyperlink so it pastes as a clickable link
// (with custom display text) into editors like Google Docs.
// Falls back to copying plain text where the rich clipboard API is unavailable.
export const copyMrLink = async (mr) => {
	const label = formatMrLinkLabel(mr);
	const url = getMRLink(mr);
	if (!url) return false;

	const html = `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
	const text = `${label} (${url})`;
	const copied = await copyTextToClipboard(text, { html });

	if (!copied) {
		console.error(
			`Failed to copy MR link (iid=${mr.iid}, project=${mr.projectPath}, url=${url})`,
		);
	}

	return copied;
};

// Default repo -> local clone path mapping used by the "copy checkout command"
// feature. Editable in Settings; supports any number of repos.
export const DEFAULT_REPO_PATHS = [
	{
		repo: "r2-gateway-worker",
		path: "/Users/schachte/Documents/repos/r2-gateway-worker",
	},
	{
		repo: "r2-metadata-worker",
		path: "/Users/schachte/Documents/repos/r2-metadata-worker",
	},
	{ repo: "r2-core", path: "/Users/schachte/Documents/repos/r2-core" },
];

// The repo name is the last segment of the project path, e.g.
// "cloudflare/r2/r2-core" -> "r2-core".
export const repoNameFromMr = (mr) => {
	const projectPath = mr?.projectPath || "";
	return projectPath.split("/").filter(Boolean).pop() || "";
};

// Look up the configured local path for an MR's repo (case-insensitive match
// on repo name). Returns undefined if no entry is configured.
export const localPathForMr = (mr, repoPaths = DEFAULT_REPO_PATHS) => {
	const name = repoNameFromMr(mr).toLowerCase();
	if (!name) return undefined;
	const entry = (repoPaths || []).find(
		(r) =>
			(r.repo || "").trim().toLowerCase() === name && (r.path || "").trim(),
	);
	return entry?.path?.trim();
};

// Build a shell command that fetches and checks out the MR's source branch in
// the configured local repo path. Returns undefined if branch/path is missing.
export const buildCheckoutCommand = (mr, repoPaths = DEFAULT_REPO_PATHS) => {
	const branch = mr?.sourceBranch;
	const path = localPathForMr(mr, repoPaths);
	if (!branch || !path) return undefined;
	return `cd "${path}" && git fetch origin && git checkout "${branch}"`;
};

// Copy the checkout command to the clipboard as plain text. Returns true on
// success, false otherwise.
export const copyCheckoutCommand = async (
	mr,
	repoPaths = DEFAULT_REPO_PATHS,
) => {
	const cmd = buildCheckoutCommand(mr, repoPaths);
	if (!cmd) return false;
	const copied = await copyTextToClipboard(cmd);
	if (!copied) {
		console.error(
			`Failed to copy checkout command (iid=${mr.iid}, project=${mr.projectPath})`,
		);
	}
	return copied;
};

export const getCIStatusInfo = (pipeline) => {
	if (!pipeline) return null;

	switch (pipeline.status) {
		case "success":
			return {
				icon: "CheckCircle2",
				color: "text-green-500 dark:text-green-400",
			};
		case "failed":
			return { icon: "XCircle", color: "text-red-500 dark:text-red-400" };
		case "pending":
		case "running":
			return { icon: "CircleDot", color: "text-[var(--color-text-primary)]" };
		default:
			return null;
	}
};
